<template>
    <Modal
        ref="modal"
        title="Recharge Budget Entry"
        icon="cash-multiple"
        :size="eventSplits.length > 1 ? 'is-xxxlarge' : undefined"
        paddingless
    >
        <div
            class="tw-grid"
            :class="{
                'tw-grid-cols-2': eventSplits.length > 1,
            }"
        >
            <!-- Main (simple) recharge -->
            <div>
                <FormField label="Events" class="tw-px-4 tw-pt-3">
                    <SmartSearchDropdown
                        v-model="events"
                        type="events"
                        placeholder="Required"
                        :items="
                            (
                                Object.values(useDataStore().get('account.events') as Record<string, Event>) as Event[]
                            ).filter((e) => e.currency_code === currentEvent.currency_code)
                        "
                        :loading="isLoading"
                        multiple
                        @update:selected="handleEventChange"
                    />
                </FormField>
                <hr />
                <div :key="displayKey" class="tw-grid tw-grid-cols-2 tw-gap-4 tw-px-4">
                    <FormField label="Department">
                        <SmartSearchDropdown
                            :model-value="selectedDepartment?.id"
                            type="departments"
                            placeholder="Required"
                            :items="departments"
                            :loading="isLoading"
                            @update:model-value="
                                (id) => (selectedDepartment = departments.find((d) => d.id === id) ?? null)
                            "
                        />
                    </FormField>
                    <FormField label="Subcategory" field-class="tw-relative">
                        <SmartSearchDropdown
                            v-model="selectedCategory"
                            type="categories"
                            placeholder="Required"
                            :items="selectedDepartment?.categories || []"
                            :loading="isLoading"
                        />
                        <div
                            v-if="!selectedDepartment"
                            class="tw-bg-white tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-bottom-0 tw-opacity-50"
                        ></div>
                    </FormField>
                    <div class="tw-col-span-2 tw-text-xs tw-flex tw-gap-2 tw-text-amber-500 tw-justify-center">
                        <i class="mdi mdi-alert-circle-outline"></i>
                        <span class="tw-w-[90%]">
                            If the target subcategory does not exist within the target event then it will be
                            automatically assigned
                        </span>
                    </div>
                </div>
                <hr />
                <div class="tw-grid tw-grid-cols-2 tw-gap-4 tw-px-4 tw-pb-4 tw-relative">
                    <FormField v-if="fixedAmountEnabled" label="Total Fixed Cost">
                        <Currency :model-value="String(budgetEntry?.fixed_cost ?? 0)" disabled />
                    </FormField>
                    <FormField label="Total Budgeted Cost">
                        <Currency :model-value="String(budgetEntry?.budgeted_cost ?? 0)" disabled />
                    </FormField>
                    <FormField v-if="fixedAmountEnabled" label="Fixed Recharge Amount">
                        <Currency v-model="fixedAmount" :disabled="isLoading" />
                    </FormField>
                    <FormField label="Budgeted Recharge Amount">
                        <Currency v-model="budgetedAmount" :disabled="isLoading" />
                    </FormField>
                    <div
                        v-if="!selectedCategory"
                        class="tw-absolute tw-pb-4 tw-bg-white/80 tw-top-0 tw-left-0 tw-right-0 tw-bottom-0 tw-flex tw-items-center tw-justify-center tw-select-none"
                    >
                        You need to select a subcategory to recharge this budget entry
                    </div>
                </div>
            </div>
            <!-- Split recharge if multiple events -->
            <div v-if="eventSplits.length > 1" class="tw-border-l tw-bg-slate-100 tw-relative">
                <h5 class="tw-py-3 tw-px-4 tw-m-0">Event Splits</h5>
                <AllocationSlider v-model="eventSplits" class="tw-px-3 tw-mb-3" :splits="events.length" />
                <div
                    v-for="event in events"
                    :key="event"
                    class="tw-grid tw-grid-cols-2 tw-p-3 tw-bg-white tw-rounded-md tw-mx-3 tw-mb-1"
                >
                    {{ useDataStore().get(`account.events.${event}.name`) }}
                    <div class="tw-flex tw-flex-col tw-gap-1">
                        <FormField
                            v-if="fixedAmountEnabled"
                            label="Fixed"
                            class="!tw-flex-row tw-items-center tw-justify-end tw-gap-2"
                        >
                            <Currency
                                compact
                                class="tw-w-[110px]"
                                :model-value="
                                    String((Number(fixedAmount) * (eventSplits[events.indexOf(event)] ?? 0)) / 100 || 0)
                                "
                                :min="0"
                                @update:model-value="
                                    (val) => recalculateSplits(events.indexOf(event), String(val ?? 0), fixedAmount)
                                "
                            />
                        </FormField>
                        <FormField label="Budgeted" class="!tw-flex-row tw-items-center tw-justify-end tw-gap-2">
                            <Currency
                                compact
                                class="tw-w-[110px]"
                                :model-value="
                                    String(
                                        (Number(budgetedAmount) * (eventSplits[events.indexOf(event)] ?? 0)) / 100 || 0
                                    )
                                "
                                :min="0"
                                @update:model-value="
                                    (val) => recalculateSplits(events.indexOf(event), String(val ?? 0), budgetedAmount)
                                "
                            />
                        </FormField>
                    </div>
                </div>
                <div
                    v-if="!Number(fixedAmount) && !Number(budgetedAmount)"
                    class="tw-absolute tw-p-10 tw-text-center tw-bg-white/80 tw-top-0 tw-left-0 tw-right-0 tw-bottom-0 tw-flex tw-items-center tw-justify-center tw-select-none"
                >
                    Set a recharge amount to allocate to manage the percentage split between multiple events
                </div>
                <Loader v-if="isLoading" fill />
            </div>
        </div>
        <template #footer>
            <Button @click="modal?.close()">Cancel</Button>
            <Button class="tw-ml-auto" :disabled="!isValid" type="is-primary" @click="submitRecharge()">
                Create Recharge
            </Button>
        </template>
    </Modal>
</template>
<script setup lang="ts">
import Modal from '@/js/components/Modal.vue';
import Button from '@/js/components/controls/Button.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import SmartSearchDropdown from '@/js/components/controls/SmartSearchDropdown.vue';
import AllocationSlider from '@/js/components/controls/AllocationSlider.vue';
import Currency from '@/js/components/controls/Currency.vue';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { Eventbus } from '@/js/utils/eventbus.ts';
import { useDataStore } from '@/js/stores/DataStore.js';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';
import { alert } from '@/js/composables/useModals.ts';
import axios, { AxiosError } from 'axios';
import { route } from 'ziggy-js';
import { toast } from '@/js/utils.js';
import { storeToRefs } from 'pinia';
import type { Event } from '@/js/types/event.ts';
import type { Department } from '@/js/types/department.ts';
import Loader from '@/js/components/controls/Loader.vue';

onMounted(() => {
    // Listen for the event to open the modal
    Eventbus.$on('rechargeBudgetEntry', (payload) => {
        budgetEntry.value = payload.budgetEntry;
        budgetEntryType.value = payload.type;
        resetState();
        modal.value?.open();
    });
});

const modal = useTemplateRef('modal');
const events = ref<Array<number | string>>([]);
const budgetEntry = ref<null | {
    id: number;
    event_id: number;
    category_id: number;
    fixed_cost: number;
    budgeted_cost: number;
}>(null);
const isLoading = ref<boolean>(false);
const budgetEntryType = ref<string>('');
const selectedDepartment = ref<Pick<Department, 'id' | 'name' | 'categories'> | null>(null);
const selectedCategory = ref<number | null>(null);
const departments = ref<Pick<Department, 'id' | 'name' | 'categories'>[]>([]);
const displayKey = ref<number>(0);
const fixedAmount = ref<string>('0');
const budgetedAmount = ref<string>('0');
const eventSplits = ref<Array<number>>([100]);
const { current_event: currentEvent } = storeToRefs(useDataStore());

onMounted(() => {
    if (currentEvent.value) {
        events.value = [currentEvent.value.id];
        fetchDepartments();
    }
});

const fixedAmountEnabled = computed<boolean>(() => {
    return useDataStore().get('account.preferences.show_fixed_costs');
});

// Removed fixedAmount and budgetedAmount checks as CC want's -ve recharges !!!
const isValid = computed<boolean>(() => {
    const splitTotal = eventSplits.value.reduce((sum, split) => sum + split, 0);
    const fixedAmountNumber = Number(fixedAmount.value);
    const budgetedAmountNumber = Number(budgetedAmount.value);

    if (Number.isNaN(fixedAmountNumber) || Number.isNaN(budgetedAmountNumber)) {
        return false;
    }

    return (
        events.value.length > 0 &&
        selectedCategory.value !== null &&
        Number.isFinite(fixedAmountNumber) &&
        Number.isFinite(budgetedAmountNumber) &&
        (eventSplits.value.length === 1 || Math.abs(splitTotal - 100) < 0.000001)
    );
});

const resetState = () => {
    events.value = currentEvent.value ? [currentEvent.value.id] : [];
    departments.value = [];
    fixedAmount.value = '0';
    budgetedAmount.value = '0';
    handleEventChange();
};

const handleEventChange = () => {
    selectedDepartment.value = null;
    selectedCategory.value = null;
    eventSplits.value = events.value.map(() => 100 / events.value.length);
    fetchDepartments();
    // Force re-render of dropdowns as we're not using v-model for them
    displayKey.value += 1;
};

const recalculateSplits = (eventIndex: number, newValue: string, totalAmount: string) => {
    const total = Number(totalAmount);
    if (total === 0) return;

    const newPercentage = (Number(newValue) / total) * 100;
    const remaining = 100 - newPercentage;
    const otherIndices = eventSplits.value.map((_, i) => i).filter((i) => i !== eventIndex);
    const currentOtherTotal = otherIndices.reduce((sum, i) => sum + (eventSplits.value[i] ?? 0), 0);

    const newSplits = [...eventSplits.value];
    newSplits[eventIndex] = newPercentage;

    if (currentOtherTotal === 0) {
        otherIndices.forEach((i) => {
            newSplits[i] = remaining / otherIndices.length;
        });
    } else {
        otherIndices.forEach((i) => {
            newSplits[i] = ((eventSplits.value[i] ?? 0) / currentOtherTotal) * remaining;
        });
    }

    eventSplits.value = newSplits;
};

const fetchDepartments = async () => {
    isLoading.value = true;
    try {
        const { data } = await axios.get(
            route('api.account.departments', {
                events: events.value,
                type: budgetEntryType.value === 'revenueDepartment' ? 2 : 1,
            })
        );
        departments.value = data;
        if (!selectedDepartment.value && departments.value.length > 0) {
            selectedDepartment.value =
                departments.value.find((d) => {
                    return d.categories?.some((c) => c.id === budgetEntry.value?.category_id);
                }) ?? null;
            if (selectedDepartment.value) {
                selectedCategory.value = budgetEntry.value?.category_id ?? null;
            }
        }
    } catch (error) {
        if (!(error instanceof AxiosError)) {
            throw error;
        }
        await alert({
            title: 'Error fetching departments',
            message: error.response?.data?.message || 'An error occurred while fetching departments.',
        });
    } finally {
        isLoading.value = false;
    }
};

const submitRecharge = async () => {
    isLoading.value = true;
    try {
        const payload = {
            amount_fixed: fixedAmountEnabled.value ? Number(fixedAmount.value) : 0,
            amount_budgeted: Number(budgetedAmount.value),
            target_category_id: selectedCategory.value,
            targets: events.value.map((eventId, index) => ({
                event_id: eventId,
                split: eventSplits.value[index],
            })),
        };

        await axios.post(
            route('api.account.event.entries.recharge', {
                account: useDataStore().get('account.id'),
                event: useDataStore().get('current_event.id'),
                entry: budgetEntry.value?.id,
            }),
            payload
        );
        capturePosthogEvent('budget_entry_recharged');
        if (events.value.length > 1) {
            capturePosthogEvent('event_split_created');
        }

        modal.value?.close();
        toast('Success', 'Recharge created successfully', 'success');
    } catch (error) {
        if (!(error instanceof AxiosError)) {
            throw error;
        }
        await alert({
            title: 'Error creating recharge',
            message: error.response?.data?.message || 'An error occurred while creating the recharge.',
        });
    } finally {
        isLoading.value = false;
    }
};
</script>
